import { getCookie } from '@avalara/bat-apps-shared/helpers/cookies';

import type { Connector, Result } from '@avalara/types/index';
import type { MarketingData } from 'types/marketing';

import { getPartnerSignupConnectorByCode } from '@avalara/bat-apps-shared/constants';
import conf from '../../conf';
import { receivePrefilledPartnerDetails, showConnectorDetails } from '../../store/avatax-signup';
import { receiveConnectorsFail, receiveConnectorsSuccess, receiveMarketingData } from '../../store/data';
import { receivePreFilledSignupForm } from '../../store/shared-signup';
import { setFetchStatus } from '../../store/system';
import { setPartnerConnector } from '../actions';
import fetch from '../fetch';
import { checkIsAuthenticated, getAuthLogin, isProdEnv } from '../helpers';

import type { Dispatch, GetState, ThunkAction } from '@avalara/types/actions';
import type { PartnerSignupInfo, State } from '@avalara/types/reducer-states';
import type { ActiveFlow, BaseNewAccountForm, UnauthenticatedUser } from '../../../../types';

export const basePostParams = { method: 'POST' };

export const authRedirect = () => window.location.assign(`${conf.CLIENT_URL}${getAuthLogin()}`);

const getDefaultCampaignId = (activeFlow: ActiveFlow, partnerSignup: PartnerSignupInfo) => {
  const wooCommerceCampaignId = isProdEnv() ? '7015a00000100lLAAQ' : '701770000006ksRAAQ';

  switch (activeFlow) {
    case 'avatax-direct-buy':
      // We use a special default campaign for API and WooCommerce:
      if (partnerSignup && partnerSignup.code === 'API') return '7010b000001Df9EAAS';
      if (partnerSignup && partnerSignup.code === 'WOOCOMMERCE') return wooCommerceCampaignId;
      return '7010b000001cS6hAAE';
    case 'avatax-buy':
      // We use a special default campaign for WooCommerce:
      if (partnerSignup && partnerSignup.code === 'WOOCOMMERCE') return wooCommerceCampaignId;
      return '';
    case 'trial-extension':
      return '7010b0000013fklAAA';
    case 'risk-assessment':
    case 'risk-assessment-net-new':
      return '7010b000001F9RKAA0';
    case 'esb-trial':
      return '7015a000002BNjjAAG';
    default:
      return '';
  }
};

const getMarketingParams = (queryString: string, defaultCampaignId: string): MarketingData => {
  const searchParams = new URLSearchParams(queryString);
  const marketingCookie = decodeURIComponent(getCookie('_avaQv') || '');
  const parsedCookie = marketingCookie !== '' ? JSON.parse(marketingCookie) : {};
  // TODO: Remove the deprecated 'CampaignID' casing once we have confirmation it's been fully retired.
  const campaignIdFromParamsOrCookie = (
    searchParams.get('CampaignID') ||
    searchParams.get('campaignId') ||
    searchParams.get('campaignid') ||
    parsedCookie.campaignid ||
    ''
  ).trim();
  const marketingData = {
    partnerSource: (searchParams.get('partnersource') || parsedCookie.partnersource || '').trim(),
    campaignId: campaignIdFromParamsOrCookie || defaultCampaignId.trim(),
    lsmr: (searchParams.get('lsmr') || parsedCookie.lsmr || 'Direct Visitor').trim(),
    lso: (searchParams.get('lso') || parsedCookie.lso || 'Direct Visitor').trim(),
    campaignName: (searchParams.get('campaignname') || parsedCookie.campaignname || '').trim(),
    connector: (searchParams.get('connector') || parsedCookie.connector || '').trim(),
    salesRepId: (searchParams.get('rep') || parsedCookie.rep || '').trim(),
    // NOTE: Since the campaignId can come from various sources, campaignIdFromParamsOrCookie contains the campaignId that comes from the cookie or query params
    // so we know if we got a campaignId that way and can reference it if needed.
    campaignIdFromParamsOrCookie,
  };

  return marketingData;
};

export const getMarketingDataFromUrl =
  (offerDefaultCampaignId?: string): ThunkAction =>
  (dispatch: Dispatch, getState: () => State) => {
    const defaultCampaignId =
      offerDefaultCampaignId ??
      getDefaultCampaignId(getState().system.activeFlow, getState().avataxSignup.partnerSignup);
    const wooCommerceCampaignId = isProdEnv() ? '7015a00000100lLAAQ' : '701770000006ksRAAQ';

    let data = getMarketingParams(window.location.search, defaultCampaignId || '');
    // Need to set partnerSource for AvaTax with WooCommerce
    if (data.campaignId === wooCommerceCampaignId) {
      data = {
        ...data,
        partnerSource: '0014000000pr8gIAAQ',
      };
    }
    dispatch(receiveMarketingData(data));
  };

export const getQuantityFromUrl = (queryString: string): number | null => {
  const searchParams = new URLSearchParams(queryString);
  const quantity = Number.parseInt(searchParams.get('quantity') || '0', 10);
  return !Number.isNaN(quantity) && quantity > 0 && quantity <= 50 ? quantity : null;
};

export const getLoginFromUrl = () => {
  if (checkIsAuthenticated()) return;
  const searchParams = new URLSearchParams(window.location.search);
  const login = searchParams.get('login');
  if (login === 'true') authRedirect();
};

const getCustomerData = async (id: string, dispatch: Dispatch): Promise<Partial<BaseNewAccountForm> | null> => {
  const result: Result<Partial<BaseNewAccountForm>> = await fetch(dispatch)(`${conf.API_URL}/api/signup?id=${id}`);
  if (!result.ok) return null;

  // Remove non-digit chars from phone number, if provided
  const phoneData = typeof result.value.phone === 'string' ? { phone: result.value.phone.replace(/\D/g, '') } : {};
  return { ...result.value, ...phoneData };
};

// This side effect triggers the partner flow (skips the select connector screen)
// if a recognized partner code is found in the queryString. It's always called
// (even for organic signup) however
export const getSignupPartnerParams =
  (queryString: string): ThunkAction =>
  async (dispatch: Dispatch, getState: () => State): Promise<void> => {
    const searchParams = new URLSearchParams(queryString);

    const returnUrl = searchParams.get('returnUrl') || null;
    const id = searchParams.get('id');

    const partnerCode = searchParams.get('partner')?.toUpperCase() || '';
    const partnerInfo = getPartnerSignupConnectorByCode(partnerCode);

    if (partnerInfo) {
      const connectorResult = await fetch(dispatch)<Connector>(
        `${conf.API_URL}/api/connectors/${partnerInfo.connectorId}`,
      );

      if (connectorResult.ok) {
        dispatch(showConnectorDetails(connectorResult.value));
        dispatch(setPartnerConnector(connectorResult.value));
      }
    }

    const {
      data: { connectors },
      sharedSignup: { signupForm },
    } = getState();

    let customerData = signupForm;

    if (id != null) {
      const savedCustomerData = await getCustomerData(id, dispatch);
      if (savedCustomerData) customerData = { ...customerData, ...savedCustomerData };
    }

    dispatch(receivePreFilledSignupForm(customerData));

    const signupData = {
      partnerCode,
      returnUrlOverride: returnUrl,
      customerData,
    };

    const connectorFromQuery = partnerInfo ? connectors.data.find((c) => c.id === partnerInfo.connectorId) : undefined;

    dispatch(receivePrefilledPartnerDetails({ prefilledData: signupData, connectorFromQuery }));
  };

export const getConnectors = () => async (dispatch: Dispatch, getState: GetState) => {
  const {
    data: {
      connectors: { data },
    },
    system: { inProgressFetches },
  } = getState();
  if (data.length > 0 || inProgressFetches.getConnectors) return;

  dispatch(setFetchStatus({ fetchName: 'getConnectors', inProgress: true }));

  const postParams = {};
  const result: Result<{ items: Array<Connector> }> = await fetch(dispatch)(
    `${conf.API_URL}/api/connectors`,
    postParams,
  );

  if (result.ok) {
    const items = result.value.items.filter((r) => r.name.toLowerCase().indexOf('do not sell') === -1);
    dispatch(receiveConnectorsSuccess(items));
  } else {
    dispatch(receiveConnectorsFail(result.message));
  }

  dispatch(setFetchStatus({ fetchName: 'getConnectors', inProgress: false }));
};

const generateAbGroup = () => (Math.round(Math.random()) === 1 ? 'B' : 'A');

const getAbGroupFromLocalStorage = (): 'A' | 'B' => {
  if (!window.localStorage) {
    return generateAbGroup();
  }

  const maybeAbGroupFromLocalStorage = window.localStorage.getItem('avaBuyTechAbGroup');
  // The following additional condition check in the "OR" statement was a fix to resolve Flow
  // issues and resolve JSON.parse error during tests.
  return maybeAbGroupFromLocalStorage && (maybeAbGroupFromLocalStorage === 'A' || maybeAbGroupFromLocalStorage === 'B')
    ? maybeAbGroupFromLocalStorage
    : generateAbGroup();
};

type AB = 'A' | 'B';
export const initABTesting = (onSetAbGroup: (_: AB) => void, passedAbGroup: AB | null): void => {
  if (passedAbGroup) return;

  if (!window.localStorage) {
    onSetAbGroup(generateAbGroup());
    return;
  }

  const abGroup = getAbGroupFromLocalStorage();
  onSetAbGroup(abGroup);

  if (!localStorage.getItem('avaBuyTechAbGroup')) {
    localStorage.setItem('avaBuyTechAbGroup', abGroup);
  }
};

export const blockPageFromIndexing = () => () => {
  // Hide page from search crawling https://developers.google.com/search/docs/crawling-indexing/block-indexing
  const meta = document.createElement('meta');
  meta.name = 'robots';
  meta.content = 'noindex';
  document.getElementsByTagName('head')[0].appendChild(meta);
};

export const isUnauthenticatedUser = (input: unknown): input is UnauthenticatedUser =>
  typeof input === 'object' && input !== null && 'unauthenticatedUser' in input;
